import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { QuestionForm } from '@/components/post-form/QuestionForm';
import { SchedulePostMenu } from '@/components/post-form/SchedulePostMenu';
import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';

import { EditQuestionModal_PostFragment_QuestionV2 } from './graphql-types/EditQuestionModal_PostFragment';
import {
  EditQuestionModal_updateQuestion,
  EditQuestionModal_updateQuestionVariables,
} from './graphql-types/EditQuestionModal_updateQuestion';

import { EditPostModalFooter } from '../EditPostModalFooter';

const POST_FRAGMENT = gql`
  fragment EditQuestionModal_PostFragment on PostV2 {
    _id
    datePublished
    group {
      _id
    }
    organization {
      _id
    }
    sponsorData {
      isSponsoredPost
    }
    tags {
      _id
      name
    }
    embeddedImagesV2 {
      imageId
      imageProxy(input: { fit: "crop", ar: "4:3", h: 384 })
    }
    ... on QuestionV2 {
      title
      questionBody: body
      questionBodyContentState: bodyContentState {
        ...RawDraftContentStateFragment
      }
      scheduledPublishDate
      isAnnouncementScheduled
    }
    versionFiles {
      _id
      file {
        _id
        name
        isVideo
        isImage
        url
      }
    }
  }
  ${RawDraftContentStateFragmentAST}
`;

const UPDATE_QUESTION = gql`
  mutation EditQuestionModal_updateQuestion($input: UpdateQuestionInput_V2!) {
    updateQuestionV2(input: $input) {
      data {
        _id
        ...EditQuestionModal_PostFragment
      }
    }
  }
  ${POST_FRAGMENT}
`;

type QuestionFormValues = {
  title: string;
  group: string;
  images: ({ imageId: string } | { videoId: string })[];
  body: EditorState;
  isSponsoredPost: boolean;
  tags?: { _id: string; name: string }[];
  attachments: { versionFileId: string; fileName: string }[];
  scheduledPublishDate: Date | null;
  isAnnouncement: boolean;
};

export const EditQuestionModal: React.FC<{
  post: EditQuestionModal_PostFragment_QuestionV2;
  isOpen: boolean;
  onClose: () => void;
}> & { postFragment: DocumentNode } = ({ post, isOpen, onClose }) => {
  const isPublished = !!post.datePublished;
  const toast = useToast();
  const permissions = usePermissions();
  const canSchedulePosts = permissions.canDoAdminStuffAndAuthenticated;

  const methods = useForm<QuestionFormValues>({
    defaultValues: {
      title: post.title,
      body: post.questionBody
        ? EditorState.createWithContent(convertFromRaw(post.questionBody))
        : EditorState.createEmpty(),
      group: post.group?._id,
      images: post.embeddedImagesV2,
      isSponsoredPost: post.sponsorData?.isSponsoredPost,
      tags: post.tags ?? [],
      attachments: post.versionFiles.map((files) => {
        return {
          versionFileId: files._id,
          fileName: files.file?.name || 'cant-load.file',
        };
      }),
      scheduledPublishDate: post.scheduledPublishDate
        ? new Date(post.scheduledPublishDate)
        : null,
      isAnnouncement: post.isAnnouncementScheduled,
    },
  });
  const { handleSubmit, formState, control } = methods;

  const [updateQuestionFunc] = useMutation<
    EditQuestionModal_updateQuestion,
    EditQuestionModal_updateQuestionVariables
  >(UPDATE_QUESTION, {
    refetchQueries: ['DraftsPage_GetMyDraftPosts', 'AccountMenu_Query'],
  });

  const onSubmit = async (params: QuestionFormValues) => {
    try {
      await updateQuestionFunc({
        variables: {
          input: {
            questionPostId: post._id,
            ...(post.datePublished
              ? {}
              : { isAnnouncement: params.isAnnouncement }),
            data: {
              groupId: params.group,
              versionFileIds: params.attachments.map(
                (file) => file.versionFileId
              ),
              embeddedImageIds: params.images
                .filter((id): id is { imageId: string } => 'imageId' in id)
                .map((id) => id.imageId),
              tagIds: params.tags?.map((tag) => tag._id),
              title: params.title,
              body: convertToRaw(params.body.getCurrentContent()),
              scheduledPublishDate: post.datePublished
                ? undefined
                : params.scheduledPublishDate?.toISOString() ?? null,
            },
          },
        },
      });
      onClose();

      toast({
        description: !!params.scheduledPublishDate
          ? `Question scheduled for ${dayjs(params.scheduledPublishDate).format(
              'MMM D, h:mm A'
            )}`
          : 'Question updated',
        status: 'success',
      });
    } catch (err) {
      toast({
        status: 'error',
        description: 'Failed to update question',
      });
      logError(err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        methods.reset();
        onClose();
      }}
      size="xl"
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isPublished ? 'Edit Question' : 'Edit Draft Question'}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px="6" py="4">
          <FormProvider {...methods}>
            <QuestionForm
              organizationId={post.organization?._id}
              initialGroupId={post.group?._id}
              originalImages={post.embeddedImagesV2.filter(
                (
                  image
                ): image is {
                  imageId: string;
                  imageProxy: string;
                  __typename: 'PostEmbeddedImage';
                } => {
                  return !!image.imageProxy;
                }
              )}
              isEditing
              isPublished={!!post.datePublished}
            />
          </FormProvider>
        </ModalBody>
        <EditPostModalFooter>
          <HStack>
            {!isPublished && canSchedulePosts && (
              <Controller
                name="scheduledPublishDate"
                control={control}
                render={({ field: { onBlur, onChange, ref, value, name } }) => (
                  <SchedulePostMenu
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    isDisabled={formState.isSubmitted && !formState.isValid}
                    validate={() => {
                      if (!value) {
                        methods.setError(name, {
                          message: 'A date and time must be specified',
                        });
                        return false;
                      } else {
                        methods.clearErrors(name);
                        return true;
                      }
                    }}
                    validationMessage={
                      formState.errors.scheduledPublishDate?.message
                    }
                    shouldRestrictUnsettingValue
                  />
                )}
              />
            )}
            <Button
              isDisabled={formState.isSubmitted && !formState.isValid}
              onClick={handleSubmit(onSubmit)}
            >
              Save
            </Button>
          </HStack>
        </EditPostModalFooter>
      </ModalContent>
    </Modal>
  );
};

EditQuestionModal.postFragment = POST_FRAGMENT;
